// components
import { EditedTaxFieldInputContainer } from './EditedTaxField.style';

// actions
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import type { TaxTotalAmountFieldProps } from '../types';
import { useEditableProp } from './helpers';
import { PriceInput } from '@interstate/components/PriceInput';
import { createSelectOnFocusHandler, useSelectOnFocusEnabled } from '@makemydeal/dr-shared-components/select-on-focus';
import { formatNumberDecimals } from '@makemydeal/dr-common-utils';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import { dealLockHooks } from '@makemydeal/dr-dash-components';

import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { convertToNumber } from '../../../utils/formatUtils';
import { useEffect, useRef, useState } from 'react';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { TAXES_NUMERIC_MAX_VALUE_MESSAGE } from '../constants';

const InputLabelStyle = {
    width: '9rem',
    '.ids-input-component-label': {
        justifyContent: 'end !important'
    }
};

const MAX_VAL = 999999.99;

export default function TaxItemMonthlyTaxField<Props extends TaxTotalAmountFieldProps>({ data, handleBlur }: Props) {
    const initialMonthlyTax = data.monthlyTax;

    const formattedMonthlyTax = formatNumberDecimals(initialMonthlyTax);
    const [monthlyTax, setMonthlyTax, isEdited] = useEditableProp(formattedMonthlyTax, data.manualChanges?.taxAmount);
    const prevValueBeforeBlur = useRef<string>(monthlyTax || '');
    const isBlurInProgress = useRef(false);

    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isCTPropertyTaxEnabled = useSelector(featureToggleSelectors.isCTPropertyTaxCalculationEnabled);
    const isAutoSelectEnabled = useSelectOnFocusEnabled(featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap);
    const fieldLabel = isCTPropertyTaxEnabled ? 'Monthly Tax Amount' : 'Monthly Amount';
    const fieldErrorKey = isCTPropertyTaxEnabled ? `${fieldLabel} ${data.taxName}` : fieldLabel;
    const isMonthlyTaxValid = useSelector(dealScreenErrorSelectors.getFieldValidationError(DealTabs.TAXES, fieldErrorKey));
    const dispatch = useDispatch();
    const [hasError, setHasError] = useState(false);
    const isLocked = dealLockHooks.useIsDealLocked();

    const validateAndSetAmount = (): boolean => {
        if (!monthlyTax && !initialMonthlyTax) {
            setMonthlyTax('0');
            prevValueBeforeBlur.current = '0';
            return false;
        }
        return true;
    };

    const handleBlurWrapper = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        isBlurInProgress.current = true;
        if (!validateAndSetAmount()) return;
        prevValueBeforeBlur.current = e.target.value;
        handleBlur(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (!validateAndSetAmount()) return;
            const formatedInput = convertToNumber(e.currentTarget.value);
            setMonthlyTax(formatNumberDecimals(formatedInput));
            handleBlur({ target: { value: formatedInput.toString() } });
            prevValueBeforeBlur.current = formatedInput.toString();
        }
    };

    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        if (isBlurInProgress.current) {
            isBlurInProgress.current = false;
            return;
        }
        dispatch(
            offerActionCreators.updatePaymentCallPendingState(
                convertToNumber(e.target.value) !== convertToNumber(prevValueBeforeBlur.current)
            )
        );
        setMonthlyTax(e.target.value);
    };

    useEffect(() => {
        const isValid = convertToNumber(monthlyTax) > MAX_VAL;
        setHasError(isValid);
        if (isDealScreenRedDotErrorsEnabled && isMonthlyTaxValid !== isValid) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.TAXES, fieldErrorKey, isValid));
        }
    }, [monthlyTax]);

    return (
        <EditedTaxFieldInputContainer $isEdited={isEdited}>
            <PriceInput
                allowDecimal={true}
                allowNegative={false}
                autoInsertCommas={true}
                data-testid="monthly-tax-item-input"
                decimalMaxLength={2}
                id="monthly-tax-item-input"
                label={fieldLabel}
                name="monthly-tax-item-summary"
                onBlur={handleBlurWrapper}
                onFocus={createSelectOnFocusHandler(isAutoSelectEnabled)}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                sx={InputLabelStyle}
                textAlign="right"
                value={monthlyTax}
                size="sm"
                hasError={hasError}
                errorMessage={TAXES_NUMERIC_MAX_VALUE_MESSAGE}
                disabled={isLocked}
            />
        </EditedTaxFieldInputContainer>
    );
}
